import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getEventBySlug } from '../lib/api/events';
import { getMyApplications } from '../lib/api/applications';
import MissionApplicationForm from '../components/MissionApplicationForm';
import MinimalEventApplicationForm from '../components/MinimalEventApplicationForm';
import type { EventWithDetails } from '../types/database';
import type { ApplicationWithDetails } from '../types/database';
import { captureError } from '../lib/capture-error';
import { isContactInfoOnlyEvent } from '../lib/event-application';
import './EventApplicationPage.css';

const EventApplicationPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [existingApplication, setExistingApplication] = useState<ApplicationWithDetails | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEvent = async () => {
      if (!slug) {
        setError('Event not found');
        setLoading(false);
        return;
      }

      try {
        const eventData = await getEventBySlug(slug);
        if (!eventData) {
          setError('Event not found');
          setLoading(false);
          return;
        }

        setEvent(eventData);
      } catch (err) {
          captureError(err, 'Error loading event');
          setError('Failed to load event');
          setLoading(false);
        } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [slug]);

  // Check if user already applied to this event
  useEffect(() => {
    if (!event || !user || event.is_parent_event || event.is_legacy_event) {
      setExistingApplication(null);
      setCheckingExisting(false);
      return;
    }
    let cancelled = false;
    setCheckingExisting(true);
    getMyApplications(user.id)
      .then((applications) => {
        if (cancelled) return;
        const found = applications.find((a) => a.event_id === event.id) ?? null;
        setExistingApplication(found);
      })
      .catch(() => {
        if (!cancelled) setExistingApplication(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingExisting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [event, user]);

  const initialSelectedOptions = useMemo(() => {
    const optionId = searchParams.get('option');
    if (!optionId || !event?.signup_options?.length) return [];
    const isValid = event.signup_options.some((opt) => opt.id === optionId);
    return isValid ? [optionId] : [];
  }, [searchParams, event?.signup_options]);

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading event information...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <h1>Event Not Found</h1>
          <p className="text-muted">{error || 'The requested event could not be found.'}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/events')}
          >
            <i className="fas fa-calendar-alt me-2" aria-hidden></i>
            View All Events
          </button>
        </div>
      </div>
    );
  }

  // Parent events cannot be applied to - redirect to event detail page
  if (event.is_parent_event) {
    return (
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body text-center">
                <h1 className="mb-3">Choose a Location</h1>
                <p className="text-muted mb-4">
                  This event takes place in multiple locations. Please select a location on the event page to apply.
                </p>
                <Link to={`/events/${event.slug}`} className="btn btn-primary btn-lg">
                  <i className="fas fa-map-marker-alt me-2" aria-hidden></i>
                  View Event & Select Location
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Legacy event redirect
  if (event.is_legacy_event && event.legacy_application_url) {
    return (
      <div className="container my-5">
        <div className="legacy-application-container">
          <div className="text-center mb-4">
            <h1>{event.title} - Application</h1>
            <p className="text-muted">
              This event uses an external application system.
            </p>
          </div>

          <div className="legacy-redirect-card">
            <div className="card">
              <div className="card-body text-center">
                <div className="mb-4">
                  <i className="fas fa-external-link-alt fa-3x text-primary"></i>
                </div>
                <h3 className="card-title">External Application Required</h3>
                <p className="card-text">
                  You will be redirected to the external application form for this event.
                </p>
                <div className="mt-4">
                  <a
                    href={event.legacy_application_url}
                    className="btn btn-primary btn-lg me-3"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fas fa-external-link-alt me-2" aria-hidden></i>
                    Continue to Application
                  </a>
                  <button
                    className="btn btn-outline-secondary btn-lg"
                    onClick={() => navigate(`/events/${event.slug}`)}
                  >
                    <i className="fas fa-arrow-left me-2" aria-hidden></i>
                    Back to Event Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Logged-in user already applied to this event: show status page
  if (existingApplication) {
    const status = existingApplication.status;
    const statusLabel = status.replace(/_/g, ' ');
    return (
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body text-center">
                <h1 className="mb-3">You&apos;ve already applied</h1>
                <p className="text-muted mb-4">
                  You have an existing application for <strong>{event.title}</strong>.
                </p>
                <p className="mb-4">
                  <span className="badge bg-secondary fs-6">{statusLabel}</span>
                </p>
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  <Link to="/my-applications" className="btn btn-primary">
                    <i className="fas fa-clipboard-list me-2" aria-hidden></i>
                    View my applications
                  </Link>
                  <Link to={`/events/${event.slug}`} className="btn btn-outline-secondary">
                    <i className="fas fa-info-circle me-2" aria-hidden></i>
                    Event details
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Still checking whether user has applied
  if (checkingExisting) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  // Confirmation "Questions?" contact from event
  const coordinatorContact =
    event.confirmation_contact_email?.trim()
      ? {
          name: event.confirmation_contact_name?.trim() || undefined,
          email: event.confirmation_contact_email.trim(),
          phone: event.confirmation_contact_phone?.trim() || undefined,
        }
      : undefined;

  // Ready to show the form
  return (
    <div className="container my-5">
      <h1 className="text-center">Mission Trip Application</h1>
      <p className="text-center text-muted mb-4">
        {isContactInfoOnlyEvent(event)
          ? <>Share your contact details to register for <strong>{event.title}</strong>.</>
          : <>Apply to join us on <strong>{event.title}</strong>.</>}
      </p>
      {isContactInfoOnlyEvent(event) ? (
        <MinimalEventApplicationForm
          eventId={event.id}
          eventSlug={event.slug}
          eventTitle={event.title}
          coordinatorContact={coordinatorContact ?? null}
          signupOptions={event.signup_options || []}
          signupOptionMode={event.signup_option_mode || 'single'}
          initialSelectedOptions={initialSelectedOptions}
        />
      ) : (
        <MissionApplicationForm
          eventId={event.id}
          eventSlug={event.slug}
          eventTitle={event.title}
          coordinatorContact={coordinatorContact ?? null}
          signupOptions={event.signup_options || []}
          signupOptionMode={event.signup_option_mode || 'single'}
          initialSelectedOptions={initialSelectedOptions}
        />
      )}
    </div>
  );
};

export default EventApplicationPage;
